import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

import { LEARNING_HUB_MODULE_PATH } from '../../definitions/learning-hub.constants';
import {
  LearningHubArticleMeta,
  LearningHubCategories,
  LearningHubCategory,
  LearningHubLanguage
} from '../../definitions/learning-hub.models';
import {
  categoryArticles,
  categoryChildren,
  categoryCommands,
  navigationSections,
  staffPickArticles
} from '../../utils/learning-hub.utils';

interface SidebarSection {
  category: LearningHubCategory;
  link: string[];
  children: { category: LearningHubCategory; link: string[] }[];
}

/**
 * Side column of the category, search and article pages (left column of the old site): search, the articles of
 * the current category (article page), the 8 sections with the children of the active one, and the Staff Pick.
 */
@Component({
  selector: 'app-learning-hub-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnChanges {
  @Input() lang: LearningHubLanguage;
  @Input() tree: LearningHubCategories;
  @Input() articles: LearningHubArticleMeta[] = [];
  /** Category of the page (a section or a child). */
  @Input() activeKey: string | null = null;
  /** On the article page: list the articles of `activeKey` and highlight this one. */
  @Input() currentArticleId: number | null = null;

  readonly modulePath = LEARNING_HUB_MODULE_PATH;
  sections: SidebarSection[] = [];
  activeParent: string | null = null;
  current: LearningHubCategory | null = null;
  currentArticles: LearningHubArticleMeta[] = [];
  staffPicks: LearningHubArticleMeta[] = [];

  ngOnChanges(): void {
    if (!this.tree) {
      return;
    }
    const categories = this.tree.categories;
    this.current = categories.find((category: LearningHubCategory) => category.key === this.activeKey) || null;
    this.activeParent = this.current ? this.current.parent || this.current.key : null;
    this.sections = navigationSections(this.tree)
      .filter((category: LearningHubCategory) => (category.articles[this.lang] || []).length > 0)
      .map((category: LearningHubCategory) => ({
        category,
        link: categoryCommands(category.key, categories),
        children:
          category.key === this.activeParent
            ? categoryChildren(category, categories, this.lang).map((child: LearningHubCategory) => ({
              category: child,
              link: categoryCommands(child.key, categories)
            }))
            : []
      }));
    this.currentArticles =
      this.currentArticleId !== null && this.current ? categoryArticles(this.current, this.articles, this.lang) : [];
    this.staffPicks = staffPickArticles(this.tree, this.articles, this.lang);
  }
}
